import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartBarIcon,
  UserGroupIcon,
  CpuChipIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  UserIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { toast } from 'react-toastify';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const ProfilingAnalyticsDashboard = ({ userRole }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalProfiles: 0,
    averageScore: 0,
    categoryAverages: {},
    scoreDistribution: {},
    recentCompletions: [],
    topPerformers: [],
  });
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [topPerformersPage, setTopPerformersPage] = useState(1);
  const [recentCompletionsPage, setRecentCompletionsPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, profile: null, type: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDropdownMenu, setShowDropdownMenu] = useState({});
  const [exportModal, setExportModal] = useState({ isOpen: false, type: null }); // 'topPerformers' or 'recentCompletions'
  const recordsPerPage = 5;

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const profilesRef = collection(firestore, 'entrepreneurProfiles');
      const profilesSnapshot = await getDocs(profilesRef);
      const profilesData = profilesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const clientsRef = collection(firestore, 'clients');
      const clientsSnapshot = await getDocs(clientsRef);
      const clientsById = clientsSnapshot.docs.reduce((acc, docSnap) => {
        acc[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        return acc;
      }, {});

      const enrichedProfiles = profilesData.map(profile => ({
        ...profile,
        client: clientsById[profile.id] || null,
      }));

      const calculatedAnalytics = calculateAnalytics(enrichedProfiles);
      setAnalytics(calculatedAnalytics);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const calculateAnalytics = profiles => {
    if (profiles.length === 0) {
      return {
        totalProfiles: 0,
        averageScore: 0,
        categoryAverages: {},
        scoreDistribution: {},
        recentCompletions: [],
        topPerformers: [],
      };
    }

    const categoryAverages = {
      personality: 0,
      mindset: 0,
      capabilities: 0,
      businessStage: 0,
    };

    let totalScore = 0;
    let completedProfiles = 0;
    const scoreDistribution = {
      strong: 0,
      good: 0,
      developing: 0,
      needsWork: 0,
    };

    profiles.forEach(profile => {
      if (profile.overallScore > 0) {
        completedProfiles++;
        totalScore += profile.overallScore;

        if (profile.personality?.score) categoryAverages.personality += profile.personality.score;
        if (profile.mindset?.score) categoryAverages.mindset += profile.mindset.score;
        if (profile.capabilities?.score)
          categoryAverages.capabilities += profile.capabilities.score;
        if (profile.businessStage?.score)
          categoryAverages.businessStage += profile.businessStage.score;

        if (profile.overallScore >= 75) scoreDistribution.strong++;
        else if (profile.overallScore >= 60) scoreDistribution.good++;
        else if (profile.overallScore >= 45) scoreDistribution.developing++;
        else scoreDistribution.needsWork++;
      }
    });

    const averageScore = completedProfiles > 0 ? Math.round(totalScore / completedProfiles) : 0;

    Object.keys(categoryAverages).forEach(category => {
      const categoryCount = profiles.filter(p => p[category]?.score > 0).length;
      categoryAverages[category] =
        categoryCount > 0 ? Math.round(categoryAverages[category] / categoryCount) : 0;
    });

    const recentCompletions = profiles
      .filter(p => p.lastUpdated)
      .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
      .slice(0, 10);

    const topPerformers = profiles
      .filter(p => p.overallScore > 0)
      .sort((a, b) => b.overallScore - a.overallScore)
      .slice(0, 10);

    return {
      totalProfiles: profiles.length,
      averageScore,
      categoryAverages,
      scoreDistribution,
      recentCompletions,
      topPerformers,
    };
  };

  const getScoreColor = score => {
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 45) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 75) return 'Strong';
    if (score >= 60) return 'Good';
    if (score >= 45) return 'Developing';
    return 'Needs Work';
  };

  const handleDeleteProfile = profile => {
    setDeleteConfirm({ isOpen: true, profile, type: 'topPerformers' });
  };

  const handleDeleteRecentCompletion = profile => {
    setDeleteConfirm({ isOpen: true, profile, type: 'recentCompletions' });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.profile) return;

    setIsDeleting(true);
    try {
      await deleteDoc(doc(firestore, 'entrepreneurProfiles', deleteConfirm.profile.id));
      toast.success('Profile deleted successfully');
      setDeleteConfirm({ isOpen: false, profile: null, type: null });
      fetchAnalytics(); // Refresh data
    } catch (error) {
      toast.error('Failed to delete profile: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, profile: null, type: null });
  };

  const openExportModal = type => {
    setExportModal({ isOpen: true, type });
  };

  const closeExportModal = () => {
    setExportModal({ isOpen: false, type: null });
  };

  const getProfileData = (profile, isTopPerformers = false) => {
    const clientName = profile.userInfo
      ? `${profile.userInfo.firstname || ''} ${profile.userInfo.lastname || ''}`.trim() ||
        profile.userInfo.email ||
        profile.id
      : profile.client
        ? `${profile.client.firstname || ''} ${profile.client.lastname || ''}`.trim() ||
          profile.client.email ||
          profile.id
        : profile.id;
    const organization = profile.userInfo?.organization || profile.client?.organization || '—';
    const coach = isTopPerformers
      ? profile.client?.assignedCoachName || profile.client?.assigned || '—'
      : null;
    const stage = profile.userInfo?.businessStage || profile.client?.businessStage || '—';
    const score = profile.overallScore;
    const status = getScoreStatus(profile.overallScore);
    const lastUpdated = profile.lastUpdated
      ? new Date(profile.lastUpdated).toLocaleDateString()
      : 'Never';

    return isTopPerformers
      ? { clientName, organization, coach, stage, score, status, lastUpdated }
      : { clientName, organization, stage, score, status, lastUpdated };
  };

  const handleExport = async format => {
    const isTopPerformers = exportModal.type === 'topPerformers';
    const data = isTopPerformers ? analytics.topPerformers : analytics.recentCompletions;
    const fileName = isTopPerformers ? 'top_performers' : 'recent_completions';
    const dateStr = new Date().toISOString().split('T')[0];

    if (format === 'excel' || format === 'csv') {
      const headers = isTopPerformers
        ? ['Client', 'Organization', 'Coach', 'Stage', 'Overall Score', 'Status', 'Last Updated']
        : ['Client', 'Organization', 'Stage', 'Overall Score', 'Status', 'Last Updated'];
      const csvContent = [
        headers.join(','),
        ...data.map(profile => {
          const profileData = getProfileData(profile, isTopPerformers);
          const values = isTopPerformers
            ? [
                profileData.clientName,
                profileData.organization,
                profileData.coach,
                profileData.stage,
                profileData.score,
                profileData.status,
                profileData.lastUpdated,
              ]
            : [
                profileData.clientName,
                profileData.organization,
                profileData.stage,
                profileData.score,
                profileData.status,
                profileData.lastUpdated,
              ];
          return values
            .map(field => (typeof field === 'string' && field.includes(',') ? `"${field}"` : field))
            .join(',');
        }),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}_${dateStr}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported successfully as ${format.toUpperCase()}`);
    } else if (format === 'json') {
      const jsonData = data.map(profile => {
        const profileData = getProfileData(profile, isTopPerformers);
        return profileData;
      });
      const jsonContent = JSON.stringify(jsonData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}_${dateStr}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exported successfully as JSON');
    } else if (format === 'pdf') {
      const headers = isTopPerformers
        ? ['Client', 'Organization', 'Coach', 'Stage', 'Overall Score', 'Status', 'Last Updated']
        : ['Client', 'Organization', 'Stage', 'Overall Score', 'Status', 'Last Updated'];

      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${isTopPerformers ? 'Top Performers' : 'Recent Completions'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #4CAF50; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>${isTopPerformers ? 'Top Performers' : 'Recent Completions'}</h1>
          <table>
            <thead>
              <tr>
                ${headers.map(h => `<th>${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data
                .map(profile => {
                  const profileData = getProfileData(profile, isTopPerformers);
                  const cells = isTopPerformers
                    ? [
                        profileData.clientName,
                        profileData.organization,
                        profileData.coach,
                        profileData.stage,
                        profileData.score,
                        profileData.status,
                        profileData.lastUpdated,
                      ]
                    : [
                        profileData.clientName,
                        profileData.organization,
                        profileData.stage,
                        profileData.score,
                        profileData.status,
                        profileData.lastUpdated,
                      ];
                  return `<tr>${cells.map(cell => `<td>${cell}</td>`).join('')}</tr>`;
                })
                .join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url);
      setTimeout(() => {
        newWindow.print();
        window.URL.revokeObjectURL(url);
      }, 250);
      toast.success('Opening PDF preview. Use Print to save as PDF.');
    } else if (format === 'word') {
      const headers = isTopPerformers
        ? ['Client', 'Organization', 'Coach', 'Stage', 'Overall Score', 'Status', 'Last Updated']
        : ['Client', 'Organization', 'Stage', 'Overall Score', 'Status', 'Last Updated'];

      let htmlContent = `
        <!DOCTYPE html>
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>${isTopPerformers ? 'Top Performers' : 'Recent Completions'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #4CAF50; color: white; }
          </style>
        </head>
        <body>
          <h1>${isTopPerformers ? 'Top Performers' : 'Recent Completions'}</h1>
          <table>
            <thead>
              <tr>
                ${headers.map(h => `<th>${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data
                .map(profile => {
                  const profileData = getProfileData(profile, isTopPerformers);
                  const cells = isTopPerformers
                    ? [
                        profileData.clientName,
                        profileData.organization,
                        profileData.coach,
                        profileData.stage,
                        profileData.score,
                        profileData.status,
                        profileData.lastUpdated,
                      ]
                    : [
                        profileData.clientName,
                        profileData.organization,
                        profileData.stage,
                        profileData.score,
                        profileData.status,
                        profileData.lastUpdated,
                      ];
                  return `<tr>${cells.map(cell => `<td>${cell}</td>`).join('')}</tr>`;
                })
                .join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], {
        type: 'application/msword',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}_${dateStr}.doc`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exported successfully as Word document');
    }

    closeExportModal();
  };

  const handleShare = (profile, type) => {
    const profileUrl = `${window.location.origin}/profiling/${profile.id}`;
    if (navigator.share) {
      navigator.share({
        title: 'Entrepreneur Profile',
        text: `Check out this entrepreneur profile`,
        url: profileUrl,
      });
    } else {
      navigator.clipboard.writeText(profileUrl);
      toast.success('Profile link copied to clipboard');
    }
    setShowDropdownMenu({});
  };

  const toggleDropdownMenu = (id, type) => {
    setShowDropdownMenu(prev => {
      const key = `${type}-${id}`;
      if (prev[key]) {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      }
      return { [key]: true };
    });
  };

  const closeAllDropdownMenus = () => {
    setShowDropdownMenu({});
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (!event.target.closest('.dropdown-container')) {
        closeAllDropdownMenus();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Pagination calculations
  const topPerformersTotalPages = Math.ceil(analytics.topPerformers.length / recordsPerPage);
  const paginatedTopPerformers = analytics.topPerformers.slice(
    (topPerformersPage - 1) * recordsPerPage,
    topPerformersPage * recordsPerPage
  );

  const recentCompletionsTotalPages = Math.ceil(
    analytics.recentCompletions.length / recordsPerPage
  );
  const paginatedRecentCompletions = analytics.recentCompletions.slice(
    (recentCompletionsPage - 1) * recordsPerPage,
    recentCompletionsPage * recordsPerPage
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2 md:gap-3">
            <ChartBarIcon className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-black md:text-3xl">
              Entrepreneur Profiling Analytics
            </h1>
          </div>
          <p className="text-sm text-black md:text-base">
            Comprehensive analytics and insights across all entrepreneur profiles
          </p>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-blue-200 bg-blue-50 p-6 shadow-sm"
          >
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-blue-600">Total Profiles</p>
                <p className="text-2xl font-bold text-blue-900">{analytics.totalProfiles}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-lg border border-green-200 bg-green-50 p-6 shadow-sm"
          >
            <div className="flex items-center">
              <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-green-600">Average Score</p>
                <p className="text-2xl font-bold text-green-900">{analytics.averageScore}%</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-lg border border-purple-200 bg-purple-50 p-6 shadow-sm"
          >
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-purple-600">Completed Profiles</p>
                <p className="text-2xl font-bold text-purple-900">
                  {analytics.recentCompletions.length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-lg border border-orange-200 bg-orange-50 p-6 shadow-sm"
          >
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-orange-600">Strong Profiles</p>
                <p className="text-2xl font-bold text-orange-900">
                  {analytics.scoreDistribution.strong}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
          >
            <h3 className="mb-6 text-xl font-semibold text-gray-900">Category Averages</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UserIcon className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">Personality</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-lg font-bold ${getScoreColor(analytics.categoryAverages.personality)}`}
                  >
                    {analytics.categoryAverages.personality}%
                  </span>
                  <div className="h-2 w-20 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full bg-blue-500"
                      style={{ width: `${analytics.categoryAverages.personality}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CpuChipIcon className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-900">Mindset</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-lg font-bold ${getScoreColor(analytics.categoryAverages.mindset)}`}
                  >
                    {analytics.categoryAverages.mindset}%
                  </span>
                  <div className="h-2 w-20 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full bg-green-500"
                      style={{ width: `${analytics.categoryAverages.mindset}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AcademicCapIcon className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-900">Capabilities</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-lg font-bold ${getScoreColor(analytics.categoryAverages.capabilities)}`}
                  >
                    {analytics.categoryAverages.capabilities}%
                  </span>
                  <div className="h-2 w-20 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full bg-purple-500"
                      style={{ width: `${analytics.categoryAverages.capabilities}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium text-gray-900">Business Stage</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-lg font-bold ${getScoreColor(analytics.categoryAverages.businessStage)}`}
                  >
                    {analytics.categoryAverages.businessStage}%
                  </span>
                  <div className="h-2 w-20 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full bg-orange-500"
                      style={{ width: `${analytics.categoryAverages.businessStage}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Score Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
          >
            <h3 className="mb-6 text-xl font-semibold text-gray-900">Score Distribution</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-700">Strong (75-100%)</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {analytics.scoreDistribution.strong}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-gray-700">Good (60-74%)</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {analytics.scoreDistribution.good}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                  <span className="text-sm text-gray-700">Developing (45-59%)</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {analytics.scoreDistribution.developing}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-700">Needs Work (0-44%)</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {analytics.scoreDistribution.needsWork}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Top Performers</h3>
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openExportModal('topPerformers')}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export
              </motion.button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border-2 border-gray-200">
            <table className="min-w-full border-collapse divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Client
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Organization
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Coach
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Stage
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Overall Score
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Status
                  </th>

                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {paginatedTopPerformers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="border-r border-gray-200 px-6 py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <UserGroupIcon className="mb-2 h-12 w-12 text-gray-300" />
                        <p className="text-sm font-medium">No top performers found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedTopPerformers.map((profile, index) => (
                    <tr
                      key={profile.id}
                      className="border-b border-gray-200 transition-colors hover:bg-blue-50/50"
                    >
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4">
                        <div className="font-semibold text-gray-900">
                          {profile.userInfo
                            ? `${profile.userInfo.firstname || ''} ${profile.userInfo.lastname || ''}`.trim() ||
                              profile.userInfo.email ||
                              profile.id
                            : profile.client
                              ? `${profile.client.firstname || ''} ${profile.client.lastname || ''}`.trim() ||
                                profile.client.email ||
                                profile.id
                              : profile.id === 'current'
                                ? 'Current User'
                                : profile.id}
                        </div>
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4 text-sm text-gray-700">
                        {profile.userInfo?.organization || profile.client?.organization || '—'}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4 text-sm text-gray-700">
                        {profile.client?.assignedCoachName || profile.client?.assigned || '—'}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4 text-sm text-gray-700">
                        {profile.client?.businessStage || '—'}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-base font-bold ${getScoreColor(profile.overallScore)}`}
                        >
                          {profile.overallScore}%
                        </span>
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4">
                        <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 ring-1 ring-green-200">
                          {getScoreStatus(profile.overallScore)}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="dropdown-container relative flex items-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setSelectedProfile(profile)}
                            className="flex items-center justify-center gap-1.5 rounded-lg border-2 border-blue-600 bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:border-blue-700 hover:bg-blue-700 hover:shadow-md"
                            title="View Profile"
                          >
                            <EyeIcon className="h-4 w-4" />
                            <span>View</span>
                          </motion.button>
                          <div className="dropdown-container relative">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => toggleDropdownMenu(profile.id, 'topPerformers')}
                              className="rounded-lg border-2 border-gray-300 bg-white p-2 text-gray-600 transition-all hover:border-gray-400 hover:bg-gray-50 hover:shadow-sm"
                            >
                              <EllipsisVerticalIcon className="h-4 w-4" />
                            </motion.button>
                            {showDropdownMenu[`topPerformers-${profile.id}`] && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => toggleDropdownMenu(profile.id, 'topPerformers')}
                                />
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-xl"
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handleShare(profile, 'topPerformers');
                                      }}
                                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                      <ShareIcon className="h-4 w-4 text-blue-600" />
                                      Share
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleDeleteProfile(profile);
                                        toggleDropdownMenu(profile.id, 'topPerformers');
                                      }}
                                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                                    >
                                      <TrashIcon className="h-4 w-4 text-red-600" />
                                      Delete
                                    </button>
                                  </div>
                                </motion.div>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {topPerformersTotalPages > 1 && (
            <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-gray-200 pt-4 sm:flex-row">
              <div className="text-sm font-medium text-gray-700">
                Showing{' '}
                <span className="font-semibold text-gray-900">
                  {(topPerformersPage - 1) * recordsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-semibold text-gray-900">
                  {Math.min(topPerformersPage * recordsPerPage, analytics.topPerformers.length)}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-gray-900">
                  {analytics.topPerformers.length}
                </span>{' '}
                results
              </div>
              <div className="flex items-center gap-1">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setTopPerformersPage(p => Math.max(1, p - 1))}
                  disabled={topPerformersPage === 1}
                  className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                    topPerformersPage === 1
                      ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                      : 'border-blue-300 bg-white text-blue-600 shadow-sm hover:border-blue-400 hover:bg-blue-50 hover:shadow-md'
                  }`}
                >
                  Previous
                </motion.button>
                <div className="flex items-center gap-1">
                  {topPerformersTotalPages <= 7 ? (
                    // Show all pages if 7 or fewer
                    Array.from({ length: topPerformersTotalPages }).map((_, i) => {
                      const pageNum = i + 1;
                      const isCurrentPage = topPerformersPage === pageNum;
                      return (
                        <motion.button
                          key={i}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setTopPerformersPage(pageNum)}
                          className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                            isCurrentPage
                              ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                              : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                          }`}
                        >
                          {pageNum}
                        </motion.button>
                      );
                    })
                  ) : (
                    // Show first, last, current, and pages around current
                    <>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setTopPerformersPage(1)}
                        className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                          topPerformersPage === 1
                            ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                        }`}
                      >
                        1
                      </motion.button>
                      {topPerformersPage > 3 && <span className="px-2 text-gray-400">...</span>}
                      {Array.from({ length: topPerformersTotalPages })
                        .map((_, i) => i + 1)
                        .filter(
                          pageNum =>
                            pageNum > 1 &&
                            pageNum < topPerformersTotalPages &&
                            Math.abs(pageNum - topPerformersPage) <= 1
                        )
                        .map(pageNum => (
                          <motion.button
                            key={pageNum}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setTopPerformersPage(pageNum)}
                            className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                              topPerformersPage === pageNum
                                ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                                : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                            }`}
                          >
                            {pageNum}
                          </motion.button>
                        ))}
                      {topPerformersPage < topPerformersTotalPages - 2 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setTopPerformersPage(topPerformersTotalPages)}
                        className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                          topPerformersPage === topPerformersTotalPages
                            ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                        }`}
                      >
                        {topPerformersTotalPages}
                      </motion.button>
                    </>
                  )}
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() =>
                    setTopPerformersPage(p => Math.min(topPerformersTotalPages, p + 1))
                  }
                  disabled={topPerformersPage === topPerformersTotalPages}
                  className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                    topPerformersPage === topPerformersTotalPages
                      ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                      : 'border-blue-300 bg-white text-blue-600 shadow-sm hover:border-blue-400 hover:bg-blue-50 hover:shadow-md'
                  }`}
                >
                  Next
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Recent Completions</h3>
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openExportModal('recentCompletions')}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export
              </motion.button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border-2 border-gray-200">
            <table className="min-w-full border-collapse divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    #
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Client
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Organization
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Stage
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Overall Score
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Status
                  </th>
                  <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Last Updated
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {paginatedRecentCompletions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="border-r border-gray-200 px-6 py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <ClipboardDocumentListIcon className="mb-2 h-12 w-12 text-gray-300" />
                        <p className="text-sm font-medium">No recent completions found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedRecentCompletions.map((profile, index) => (
                    <tr
                      key={profile.id}
                      className="border-b border-gray-200 transition-colors hover:bg-blue-50/50"
                    >
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4 text-sm font-semibold text-gray-600">
                        {(recentCompletionsPage - 1) * recordsPerPage + index + 1}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4">
                        <div className="font-semibold text-gray-900">
                          {profile.userInfo
                            ? `${profile.userInfo.firstname || ''} ${profile.userInfo.lastname || ''}`.trim() ||
                              profile.userInfo.email ||
                              profile.id
                            : profile.client
                              ? `${profile.client.firstname || ''} ${profile.client.lastname || ''}`.trim() ||
                                profile.client.email ||
                                profile.id
                              : profile.id === 'current'
                                ? 'Current User'
                                : profile.id}
                        </div>
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4 text-sm text-gray-700">
                        {profile.userInfo?.organization || profile.client?.organization || '—'}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4 text-sm text-gray-700">
                        {profile.userInfo?.businessStage || profile.client?.businessStage || '—'}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-base font-bold ${getScoreColor(profile.overallScore)}`}
                        >
                          {profile.overallScore}%
                        </span>
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4">
                        <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 ring-1 ring-green-200">
                          {getScoreStatus(profile.overallScore)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-200 px-6 py-4 text-sm text-gray-700">
                        {profile.lastUpdated
                          ? new Date(profile.lastUpdated).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="dropdown-container relative flex items-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setSelectedProfile(profile)}
                            className="flex items-center justify-center gap-1.5 rounded-lg border-2 border-blue-600 bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:border-blue-700 hover:bg-blue-700 hover:shadow-md"
                            title="View Profile"
                          >
                            <EyeIcon className="h-4 w-4" />
                            <span>View</span>
                          </motion.button>
                          <div className="dropdown-container relative">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => toggleDropdownMenu(profile.id, 'recentCompletions')}
                              className="rounded-lg border-2 border-gray-300 bg-white p-2 text-gray-600 transition-all hover:border-gray-400 hover:bg-gray-50 hover:shadow-sm"
                            >
                              <EllipsisVerticalIcon className="h-4 w-4" />
                            </motion.button>
                            {showDropdownMenu[`recentCompletions-${profile.id}`] && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() =>
                                    toggleDropdownMenu(profile.id, 'recentCompletions')
                                  }
                                />
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-xl"
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handleShare(profile, 'recentCompletions');
                                      }}
                                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                      <ShareIcon className="h-4 w-4 text-blue-600" />
                                      Share
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleDeleteRecentCompletion(profile);
                                        toggleDropdownMenu(profile.id, 'recentCompletions');
                                      }}
                                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                                    >
                                      <TrashIcon className="h-4 w-4 text-red-600" />
                                      Delete
                                    </button>
                                  </div>
                                </motion.div>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {recentCompletionsTotalPages > 1 && (
            <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-gray-200 pt-4 sm:flex-row">
              <div className="text-sm font-medium text-gray-700">
                Showing{' '}
                <span className="font-semibold text-gray-900">
                  {(recentCompletionsPage - 1) * recordsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-semibold text-gray-900">
                  {Math.min(
                    recentCompletionsPage * recordsPerPage,
                    analytics.recentCompletions.length
                  )}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-gray-900">
                  {analytics.recentCompletions.length}
                </span>{' '}
                results
              </div>
              <div className="flex items-center gap-1">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setRecentCompletionsPage(p => Math.max(1, p - 1))}
                  disabled={recentCompletionsPage === 1}
                  className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                    recentCompletionsPage === 1
                      ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                      : 'border-blue-300 bg-white text-blue-600 shadow-sm hover:border-blue-400 hover:bg-blue-50 hover:shadow-md'
                  }`}
                >
                  Previous
                </motion.button>
                <div className="flex items-center gap-1">
                  {recentCompletionsTotalPages <= 7 ? (
                    // Show all pages if 7 or fewer
                    Array.from({ length: recentCompletionsTotalPages }).map((_, i) => {
                      const pageNum = i + 1;
                      const isCurrentPage = recentCompletionsPage === pageNum;
                      return (
                        <motion.button
                          key={i}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setRecentCompletionsPage(pageNum)}
                          className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                            isCurrentPage
                              ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                              : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                          }`}
                        >
                          {pageNum}
                        </motion.button>
                      );
                    })
                  ) : (
                    // Show first, last, current, and pages around current
                    <>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setRecentCompletionsPage(1)}
                        className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                          recentCompletionsPage === 1
                            ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                        }`}
                      >
                        1
                      </motion.button>
                      {recentCompletionsPage > 3 && <span className="px-2 text-gray-400">...</span>}
                      {Array.from({ length: recentCompletionsTotalPages })
                        .map((_, i) => i + 1)
                        .filter(
                          pageNum =>
                            pageNum > 1 &&
                            pageNum < recentCompletionsTotalPages &&
                            Math.abs(pageNum - recentCompletionsPage) <= 1
                        )
                        .map(pageNum => (
                          <motion.button
                            key={pageNum}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setRecentCompletionsPage(pageNum)}
                            className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                              recentCompletionsPage === pageNum
                                ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                                : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                            }`}
                          >
                            {pageNum}
                          </motion.button>
                        ))}
                      {recentCompletionsPage < recentCompletionsTotalPages - 2 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setRecentCompletionsPage(recentCompletionsTotalPages)}
                        className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                          recentCompletionsPage === recentCompletionsTotalPages
                            ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'
                        }`}
                      >
                        {recentCompletionsTotalPages}
                      </motion.button>
                    </>
                  )}
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() =>
                    setRecentCompletionsPage(p => Math.min(recentCompletionsTotalPages, p + 1))
                  }
                  disabled={recentCompletionsPage === recentCompletionsTotalPages}
                  className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
                    recentCompletionsPage === recentCompletionsTotalPages
                      ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                      : 'border-blue-300 bg-white text-blue-600 shadow-sm hover:border-blue-400 hover:bg-blue-50 hover:shadow-md'
                  }`}
                >
                  Next
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>

        {selectedProfile && (
          <ProfileDetailModal profile={selectedProfile} onClose={() => setSelectedProfile(null)} />
        )}

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Profile"
          message={`Are you sure you want to delete this entrepreneur profile? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />

        {/* Export Format Selection Modal */}
        <AnimatePresence>
          {exportModal.isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
              onClick={closeExportModal}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={e => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
              >
                <div className="flex items-center justify-between border-b border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900">Select Export Format</h3>
                  <button
                    onClick={closeExportModal}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6">
                  <p className="mb-4 text-sm text-gray-600">
                    Choose your preferred export format for{' '}
                    {exportModal.type === 'topPerformers' ? 'Top Performers' : 'Recent Completions'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExport('excel')}
                      className="flex flex-col items-center gap-2 rounded-lg border-2 border-green-200 bg-green-50 p-4 transition-colors hover:border-green-400 hover:bg-green-100"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-600">
                        <ArrowDownTrayIcon className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Excel/CSV</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExport('pdf')}
                      className="flex flex-col items-center gap-2 rounded-lg border-2 border-red-200 bg-red-50 p-4 transition-colors hover:border-red-400 hover:bg-red-100"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600">
                        <DocumentTextIcon className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">PDF</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExport('json')}
                      className="flex flex-col items-center gap-2 rounded-lg border-2 border-blue-200 bg-blue-50 p-4 transition-colors hover:border-blue-400 hover:bg-blue-100"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600">
                        <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">JSON</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExport('word')}
                      className="flex flex-col items-center gap-2 rounded-lg border-2 border-purple-200 bg-purple-50 p-4 transition-colors hover:border-purple-400 hover:bg-purple-100"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-600">
                        <DocumentTextIcon className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Word</span>
                    </motion.button>
                  </div>
                </div>
                <div className="border-t border-gray-200 p-4">
                  <button
                    onClick={closeExportModal}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const ProfileDetailModal = ({ profile, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'personality', name: 'Personality', icon: UserIcon },
    { id: 'mindset', name: 'Mindset', icon: CpuChipIcon },
    { id: 'capabilities', name: 'Capabilities', icon: AcademicCapIcon },
    { id: 'businessStage', name: 'Business Stage', icon: BuildingOfficeIcon },
  ];

  const getScoreColor = score => {
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 45) return 'text-yellow-600';
    return 'text-red-600';
  };

  const renderCategoryDetails = (category, data) => {
    if (!data || Object.keys(data).length === 0) {
      return <p className="text-sm text-gray-500">No data available</p>;
    }

    const getFieldDisplayName = fieldName => {
      return fieldName
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/^\w/, c => c.toUpperCase());
    };

    const getFieldValue = value => {
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      return value;
    };

    const getFieldColor = value => {
      if (typeof value === 'string') {
        if (value.includes('stronglyAgree') || value.includes('expert')) return 'text-green-600';
        if (value.includes('agree') || value.includes('proficient')) return 'text-blue-600';
        if (value.includes('neutral') || value.includes('basic')) return 'text-yellow-600';
        if (value.includes('disagree') || value.includes('beginner')) return 'text-red-600';
      }
      return 'text-gray-600';
    };

    return (
      <div className="space-y-6">
        {data.score && (
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-900">Overall Score</span>
              <span className={`text-2xl font-bold ${getScoreColor(data.score)}`}>
                {data.score}%
              </span>
            </div>
          </div>
        )}

        {data.lastUpdated && (
          <div className="rounded-lg bg-blue-50 p-3">
            <p className="text-sm text-blue-700">
              <span className="font-medium">Last Updated:</span>{' '}
              {new Date(data.lastUpdated).toLocaleDateString()}
            </p>
          </div>
        )}

        {Object.entries(data).map(([key, value]) => {
          if (key === 'notes') {
            return (
              <div key={key} className="rounded-lg bg-yellow-50 p-4">
                <h4 className="mb-2 text-sm font-semibold text-yellow-800">Notes</h4>
                <p className="text-sm text-yellow-700">{value}</p>
              </div>
            );
          }
          if (key === 'score' || key === 'lastUpdated') return null;

          return (
            <div key={key} className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="mb-3 text-lg font-semibold text-gray-900">
                {getFieldDisplayName(key)}
              </h4>

              {typeof value === 'object' && value !== null ? (
                <div className="space-y-2">
                  {Object.entries(value).map(([subKey, subValue]) => (
                    <div
                      key={subKey}
                      className="flex items-center justify-between border-b border-gray-100 py-2 last:border-b-0"
                    >
                      <span className="text-sm font-medium text-gray-700">
                        {getFieldDisplayName(subKey)}
                      </span>
                      <span className={`text-sm font-semibold ${getFieldColor(subValue)}`}>
                        {getFieldValue(subValue)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Value</span>
                  <span className={`text-sm font-semibold ${getFieldColor(value)}`}>
                    {getFieldValue(value)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

        <div className="relative w-full max-w-4xl rounded-lg bg-white shadow-xl">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex flex-col space-y-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  {profile.userInfo
                    ? profile.userInfo.organization || profile.userInfo.email || profile.id
                    : profile.client
                      ? profile.client.email || profile.id
                      : profile.id === 'current'
                        ? 'Current User'
                        : profile.id}
                </h2>

                <h3 className="text-lg font-medium text-gray-700">
                  {profile.userInfo
                    ? `${profile.userInfo.firstname || ''} ${profile.userInfo.lastname || ''}`.trim() ||
                      profile.userInfo.email ||
                      profile.id
                    : profile.client
                      ? `${profile.client.firstname || ''} ${profile.client.lastname || ''}`.trim() ||
                        profile.client.email ||
                        profile.id
                      : profile.id === 'current'
                        ? 'Current User'
                        : profile.id}
                </h3>
              </div>

              <button
                onClick={onClose}
                className="rounded-lg bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-black'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {(profile.userInfo || profile.client) && (
                  <div className="rounded-lg bg-blue-50 p-4">
                    <h4 className="mb-3 text-lg font-semibold text-blue-900">User Information</h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <p className="text-sm text-blue-700">Name</p>
                        <p className="font-medium text-blue-900">
                          {profile.userInfo
                            ? `${profile.userInfo.firstname || ''} ${profile.userInfo.lastname || ''}`.trim() ||
                              'Not provided'
                            : profile.client
                              ? `${profile.client.firstname || ''} ${profile.client.lastname || ''}`.trim() ||
                                'Not provided'
                              : 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">Email</p>
                        <p className="font-medium text-blue-900">
                          {profile.userInfo?.email || profile.client?.email || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">Organization</p>
                        <p className="font-medium text-blue-900">
                          {profile.userInfo?.organization ||
                            profile.client?.organization ||
                            'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">Business Stage</p>
                        <p className="font-medium text-blue-900">
                          {profile.client?.businessStage || 'Not provided'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-gray-50 p-4">
                    <p className="text-sm text-gray-600">Overall Score</p>
                    <p className={`text-2xl font-bold ${getScoreColor(profile.overallScore)}`}>
                      {profile.overallScore}%
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4">
                    <p className="text-sm text-gray-600">Last Updated</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {profile.lastUpdated
                        ? new Date(profile.lastUpdated).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'personality' &&
              renderCategoryDetails('personality', profile.personality)}
            {activeTab === 'mindset' && renderCategoryDetails('mindset', profile.mindset)}
            {activeTab === 'capabilities' &&
              renderCategoryDetails('capabilities', profile.capabilities)}
            {activeTab === 'businessStage' &&
              renderCategoryDetails('businessStage', profile.businessStage)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilingAnalyticsDashboard;
